import { useCallback, useEffect, useState } from 'react';
import { FlatList, StyleSheet, View, type ListRenderItemInfo } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ApiError } from '../../api/client';
import { getLearningAnalysis } from '../../api/learningAnalysis';
import { AiDiagnosisCard } from '../../components/business/AiDiagnosisCard';
import { DimensionChart } from '../../components/business/DimensionChart';
import { KnowledgeGraphPreview } from '../../components/business/KnowledgeGraphPreview';
import { LearningTimeFilter } from '../../components/business/LearningTimeFilter';
import { LearningSummaryCard } from '../../components/business/LearningSummaryCard';
import { MasteryOverviewCard } from '../../components/business/MasteryOverviewCard';
import { SubjectMasteryCard } from '../../components/business/SubjectMasteryCard';
import { WeakPointList } from '../../components/business/WeakPointList';
import { AppButton } from '../../components/ui/AppButton';
import { AppText } from '../../components/ui/AppText';
import { EmptyView } from '../../components/ui/EmptyView';
import { ErrorView } from '../../components/ui/ErrorView';
import { LoadingView } from '../../components/ui/LoadingView';
import { showAppToast } from '../../components/ui/AppToast';
import { tokens } from '../../theme/tokens';
import type {
  AiDiagnosisViewModel,
  DimensionMetricViewModel,
  KnowledgeGraphEdgeViewModel,
  KnowledgeGraphNodeViewModel,
  LearningAnalysisRange,
  LearningAnalysisViewModel,
  SubjectMasteryViewModel,
  WeakPointViewModel,
} from '../../types/learningAnalysis';

type LearningAnalysisPageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'empty' }
  | { status: 'success'; analysis: LearningAnalysisViewModel };

const isAnalysisEmpty = (analysis: LearningAnalysisViewModel): boolean =>
  analysis.subjects.length === 0;

const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.code === 'unauthorized') {
      return '登录状态已失效，请重新登录';
    }
    return error.message;
  }
  return '暂时无法加载学情分析';
};

const getGraphStatusLabel = (masteryPercent: number): string => {
  if (masteryPercent >= 80) {
    return '已掌握';
  }
  if (masteryPercent >= 60) {
    return '待复习';
  }
  return '薄弱';
};

const clampDimensionScore = (score: number): number =>
  Math.min(100, Math.max(0, Math.round(score)));

const getAverageSubjectMastery = (subjects: SubjectMasteryViewModel[]): number => {
  if (!subjects.length) {
    return 0;
  }
  const totalMastery = subjects.reduce((sum, subject) => sum + subject.masteryPercent, 0);
  return clampDimensionScore(totalMastery / subjects.length);
};

export default function LearningAnalysisTabPage() {
  const router = useRouter();
  const [state, setState] = useState<LearningAnalysisPageState>({ status: 'loading' });
  const [activeRange, setActiveRange] = useState<LearningAnalysisRange>('week');

  const loadAnalysis = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const analysis = await getLearningAnalysis();
      if (isAnalysisEmpty(analysis)) {
        setState({ status: 'empty' });
        return;
      }
      setState({ status: 'success', analysis });
    } catch (error: unknown) {
      setState({ status: 'error', message: getErrorMessage(error) });
    }
  }, []);

  useEffect(() => {
    void loadAnalysis();
  }, [loadAnalysis]);

  const showDevelopmentToast = useCallback((featureName: string) => {
    showAppToast(`${featureName}功能开发中`, 'info');
  }, []);

  const onShare = useCallback(() => {
    showDevelopmentToast('轨迹分享');
  }, [showDevelopmentToast]);

  const openKnowledgeGraph = useCallback(() => {
    router.push('/knowledge-graph');
  }, [router]);

  const openGraphPractice = useCallback(() => {
    router.push('/wrong-question-practice');
  }, [router]);

  const openWeakPoint = useCallback(
    (weakPoint: WeakPointViewModel) => {
      if (weakPoint.routeKey === 'photo-search') {
        router.push('/photo-search');
      }
    },
    [router]
  );

  if (state.status === 'loading') {
    return (
      <SafeAreaView style={styles.screen}>
        <LoadingView message="正在加载学情分析" />
      </SafeAreaView>
    );
  }

  if (state.status === 'error') {
    return (
      <SafeAreaView style={styles.screen}>
        <ErrorView message={state.message} onRetry={loadAnalysis} />
      </SafeAreaView>
    );
  }

  if (state.status === 'empty') {
    return (
      <SafeAreaView style={styles.screen}>
        <EmptyView
          title="暂无学情数据"
          description="完成几次练习后，这里会展示掌握度和薄弱点。"
          actionLabel="重新加载"
          onAction={loadAnalysis}
        />
      </SafeAreaView>
    );
  }

  const { analysis } = state;
  const knowledgeGraphNodes: KnowledgeGraphNodeViewModel[] = analysis.subjects.map((subject) => ({
      id: subject.id,
      label: subject.weakPoint,
      subject: subject.subjectName,
      masteryPercent: subject.masteryPercent,
      statusLabel: getGraphStatusLabel(subject.masteryPercent),
      description: `${subject.subjectName}当前重点是${subject.weakPoint}，建议结合最近错题先完成一组短练。`,
      actionLabel: '练一组相关题',
  }));
  const knowledgeGraphEdges: KnowledgeGraphEdgeViewModel[] = knowledgeGraphNodes
    .slice(1)
    .map((node, index) => ({
      id: `${knowledgeGraphNodes[0]?.id ?? 'root'}-${node.id}`,
      sourceId: knowledgeGraphNodes[0]?.id ?? node.id,
      targetId: node.id,
      strengthLabel: index === 0 ? '核心关联' : '迁移关联',
    }))
    .filter((edge) => edge.sourceId !== edge.targetId);
  const averageSubjectMastery = getAverageSubjectMastery(analysis.subjects);
  const dimensionMetrics: DimensionMetricViewModel[] = [
    { id: 'knowledge-mastery', label: '知识掌握', score: analysis.summary.overallMasteryPercent },
    { id: 'accuracy', label: '正确率', score: averageSubjectMastery },
    { id: 'focus', label: '专注度', score: clampDimensionScore(70 + analysis.summary.streakDays * 2) },
    { id: 'study-duration', label: '学习时长', score: clampDimensionScore(analysis.summary.totalPracticeCount) },
    { id: 'progress-rate', label: '进步率', score: clampDimensionScore(analysis.summary.masteredWrongCount * 7) },
  ];
  const aiDiagnosis: AiDiagnosisViewModel = {
    title: '基于本周练习和错题记录',
    summary: '数学函数和物理力学仍是当前提升最快的突破口，建议先短时高频复盘。',
    errorCauses: ['审题时遗漏条件范围', '公式单位换算不稳定', '步骤书写缺少中间推导'],
    attackPlans: ['每天 15 分钟函数专项', '错题再练后立刻标记掌握状态', '用拍照搜题追问关键步骤'],
  };

  const renderSubject = ({ item }: ListRenderItemInfo<SubjectMasteryViewModel>) => (
    <SubjectMasteryCard
      subjectName={item.subjectName}
      masteryPercent={item.masteryPercent}
      weakPoint={item.weakPoint}
      trendLabel={item.trendLabel}
    />
  );

  return (
    <SafeAreaView style={styles.screen}>
      <FlatList
        data={analysis.subjects}
        keyExtractor={(item) => item.id}
        renderItem={renderSubject}
        contentContainerStyle={styles.content}
        ListHeaderComponent={
          <View style={styles.header}>
            <AppText variant="title" weight="bold">
              学习轨迹
            </AppText>
            <View style={styles.titleRow}>
              <AppText variant="body" tone="muted" style={styles.titleCopy}>
                追踪学科掌握度、连续学习和薄弱知识点。
              </AppText>
              <AppButton label="分享" variant="secondary" onPress={onShare} style={styles.shareButton} />
            </View>
            <LearningTimeFilter activeRange={activeRange} onChangeRange={setActiveRange} />
            <KnowledgeGraphPreview
              nodes={knowledgeGraphNodes}
              edges={knowledgeGraphEdges}
              onOpenFullscreen={openKnowledgeGraph}
              onStartPractice={openGraphPractice}
            />
            <AiDiagnosisCard diagnosis={aiDiagnosis} />
            <DimensionChart metrics={dimensionMetrics} />
            <MasteryOverviewCard summary={analysis.summary} subjects={analysis.subjects} />
            <LearningSummaryCard
              overallMasteryPercent={analysis.summary.overallMasteryPercent}
              streakDays={analysis.summary.streakDays}
              masteredWrongCount={analysis.summary.masteredWrongCount}
              totalPracticeCount={analysis.summary.totalPracticeCount}
            />
            <WeakPointList weakPoints={analysis.weakPoints} onPressWeakPoint={openWeakPoint} />
            <AppText variant="subtitle" weight="bold">
              学科掌握
            </AppText>
          </View>
        }
        ItemSeparatorComponent={Separator}
      />
    </SafeAreaView>
  );
}

function Separator() {
  return <View style={styles.separator} />;
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.lg,
    paddingBottom: tokens.spacing.xxl,
  },
  header: {
    gap: tokens.spacing.md,
    marginBottom: tokens.spacing.md,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  titleCopy: {
    flex: 1,
  },
  shareButton: {
    minHeight: 36,
  },
  separator: {
    height: tokens.spacing.md,
  },
});
